import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import defaults from './ui.defaults.json';
import {copyTemplate,copyMessageParts,formatCopy,type CopyViewport} from './copyProfiles';
export type UiConfiguration = typeof defaults & {configurationRevision?:number};
const Context = createContext<UiConfiguration>(defaults);
const CopyViewportContext=createContext<CopyViewport>('base');
let copyMedia:MediaQueryList|undefined;
const viewportMedia=()=>copyMedia??=window.matchMedia('(min-width:1101px)');
const subscribeViewport=(changed:()=>void)=>{const media=viewportMedia();media.addEventListener('change',changed);return()=>media.removeEventListener('change',changed);};
// One breakpoint for presentation; never use it to choose service or command rules.
export const useWebViewport=()=>useSyncExternalStore(subscribeViewport,()=>viewportMedia().matches,()=>false);
export function DesignProvider({ children, theme }: { children: ReactNode; theme: string }) {
  const [configuration, setConfiguration] = useState<UiConfiguration>(defaults);
  const web=useWebViewport();
  useEffect(() => {
    let live = true;
    let revision = 0;
    const adopt = (next:UiConfiguration) => {
      if (!live || next.schemaVersion !== defaults.schemaVersion) return;
      const colorCopy=Object.fromEntries(Object.entries(next.cardAppearance?.groups??{}).map(([key,value])=>['color.card.'+key,value.label]));
      const merged={...defaults,...next,tokens:{...defaults.tokens,...next.tokens},copy:{...defaults.copy,...colorCopy,...next.copy},controls:{...defaults.controls,...next.controls},assets:{...defaults.assets,...next.assets}};
      merged.controls.tombalaDefaultAmount=next.controls.tombalaDefaultAmount??next.controls.defaultAmount??defaults.controls.tombalaDefaultAmount;
      setConfiguration(old => (next.configurationRevision??0)<(old.configurationRevision??0)||JSON.stringify(old)===JSON.stringify(merged)?old:merged);
    };
    const refresh = async () => {
      const requestRevision=++revision;
      try {
        const response = await fetch('/api/ui');
        if (response.ok && live && requestRevision===revision) {
          const next = await response.json() as UiConfiguration;
          if(requestRevision===revision)adopt(next);
        }
      } catch { /* The packaged design profile remains available offline. */ }
    };
    // Reuse the game's connection for rare design changes. No per-player polling.
    const changed=(event:Event)=>{const next=(event as CustomEvent<UiConfiguration>).detail;if(next){revision++;adopt(next);}else void refresh();};
    const visible=()=>{if(!document.hidden)void refresh();};
    window.addEventListener('tombala:ui-changed',changed);
    document.addEventListener('visibilitychange',visible);
    void refresh();
    return () => { live = false; window.removeEventListener('tombala:ui-changed',changed);document.removeEventListener('visibilitychange',visible); };
  }, []);
  useEffect(() => {
    const tokens = { ...configuration.tokens, ...(configuration.themes as Record<string, Record<string,string>>)[theme] };
    for (const [name, value] of Object.entries(tokens)) document.documentElement.style.setProperty(name, value);
  }, [configuration, theme]);
  useEffect(()=>{document.title=copyTemplate(configuration.copy,theme==='gameadmin'?'page.adminTitle':'page.playerTitle',web?'web':'mobile');},[configuration,theme,web]);
  return <Context.Provider value={configuration}><CopyViewportContext.Provider value={web?'web':'mobile'}>{children}</CopyViewportContext.Provider></Context.Provider>;
}
export const useUi = () => useContext(Context);
// Local preview uses the real presentation context without polling or changing
// document tokens / the saved profile.
export function UiPreview({configuration,children,viewport}:{configuration:UiConfiguration;children:ReactNode;viewport?:CopyViewport}){const inherited=useContext(CopyViewportContext);return <Context.Provider value={configuration}><CopyViewportContext.Provider value={viewport??inherited}>{children}</CopyViewportContext.Provider></Context.Provider>;}
export function useCopy() {
  const ui = useUi(),viewport=useContext(CopyViewportContext);
  return (key: string, values: Record<string, string | number> = {}) => {
    const message=copyMessageParts(key);
    return formatCopy(copyTemplate(ui.copy,message?.key??key,viewport),message?.values??values);
  };
}
